import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { RequestStatusLabel } from '../lib/labels'
import ListContainer from '../ui/ListContainer'
import { useToast } from '../ui/ToastProvider'
import { usePerspective } from '../hooks/usePerspective'
import { useAuth } from '../auth/AuthContext'

// 설계 의뢰 목록 페이지
// - 서버 페이지네이션/정렬 사용, 기본 정렬은 createdAt,DESC
// - CSV 내보내기, 벌크 재배정 포함
export default function RequestsPage() {
    const queryClient = useQueryClient()
    const { show } = useToast()
    const { user: currentUser } = useAuth() || { user: null }
    const [searchParams, setSearchParams] = useSearchParams()
    const [q, setQ] = useState(searchParams.get('q') || '')
    const [status, setStatus] = useState(searchParams.get('status') || 'ALL')
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size, setSize] = useState(Number(searchParams.get('size') || '10'))
    // 기본 정렬: 생성일 내림차순
    // - BE SortWhitelist는 "-createdAt" 포맷을 기본 지원 (DESC)
    const [sort, setSort] = useState(searchParams.get('sort') || '-createdAt')
    const [selected, setSelected] = useState([])
    const [orgScopeId, setOrgScopeId] = useState('')
    const [orgScopeOptions, setOrgScopeOptions] = useState([])
    const [bulkManagerId, setBulkManagerId] = useState('')
    const { view, setView, isOperator } = usePerspective()

    // 보험사 사용자 전용: 보기 범위 (나에게 배정된 의뢰 / 우리 지점 전체 의뢰)
    const isInsurerUser = useMemo(() => {
        const roles = currentUser?.roles || []
        return roles.some(r => String(r).startsWith('INSURER_'))
    }, [currentUser])
    const isInsurerAdmin = useMemo(() => {
        const roles = currentUser?.roles || []
        return roles.includes('INSURER_MASTER_ADMIN')
            || roles.includes('INSURER_MASTER_DEPUTY_ADMIN')
            || roles.includes('INSURER_LEADER')
            || roles.includes('INSURER_DEPUTY_LEADER')
            || roles.includes('INSURER_OTHER_ADMIN')
            || roles.includes('INSURER_BRANCH_MANAGER')
            || roles.includes('INSURER_DEPUTY_BRANCH_MANAGER')
    }, [currentUser])
    const [insurerScope, setInsurerScope] = useState(() => {
        if (isInsurerUser) {
            return isInsurerAdmin ? 'BRANCH' : 'MINE'
        }
        return 'DEFAULT'
    })
    // 관점별 확장 필터
    const [designer, setDesigner] = useState('') // GA 관점
    const [insurer, setInsurer] = useState('')   // GA 관점
    const [periodStart, setPeriodStart] = useState('') // 공통
    const [periodEnd, setPeriodEnd] = useState('')     // 공통
    const [periodPreset, setPeriodPreset] = useState('') // '', today, 7d, 30d

    // 서버 페이징 정보
    const [pagination, setPagination] = useState({
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
        size: 10,
        hasNext: false,
        hasPrevious: false
    })

    const dq = useDebouncedValue(q, 300)

    useEffect(() => {
        const next = new URLSearchParams()
        if (dq) next.set('q', dq)
        if (status && status !== 'ALL') next.set('status', status)
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 10) next.set('size', String(size))
        if (sort && sort !== '-createdAt') next.set('sort', sort)
        if (designer) next.set('designer', designer)
        if (insurer) next.set('insurer', insurer)
        if (periodStart) next.set('from', periodStart)
        if (periodEnd) next.set('to', periodEnd)
        setSearchParams(next, { replace: true })
    }, [dq, status, page, size, sort, designer, insurer, periodStart, periodEnd, setSearchParams])

    const listQuery = useQuery({
        queryKey: ['requests', { dq, status, page, size, sort, designer, insurer, periodStart, periodEnd, view, insurerScope, orgScopeId }],
        queryFn: async () => {
            const params = {}
            if (status !== 'ALL') params.status = status
            if (dq) params.q = dq
            params.page = page - 1 // 서버는 0-based page를 기대
            params.size = size
            params.sort = sort
            params.view = view
            if (orgScopeId && (view === 'GA' || view === 'ALL')) {
                params.orgId = Number(orgScopeId)
            }
            if (view === 'INSURER' && isInsurerUser) {
                params.scope = insurerScope
            }
            if (designer) params.designer = designer
            if (insurer) params.insurer = insurer
            if (periodStart) params.from = periodStart
            if (periodEnd) params.to = periodEnd
            const res = await api.get('/requests', { params })

            const data = res.data?.data || res.data
            if (Array.isArray(data)) {
                // 배열로 오는 경우 (레거시)
                setPagination({
                    totalElements: data.length,
                    totalPages: 1,
                    currentPage: 0,
                    size: size,
                    hasNext: false,
                    hasPrevious: false
                })
                return data
            } else {
                // 페이지네이션 정보가 있는 경우
                setPagination({
                    totalElements: data.totalElements || 0,
                    totalPages: data.totalPages || 1,
                    currentPage: data.number || 0,
                    size: data.size || size,
                    hasNext: data.hasNext || false,
                    hasPrevious: data.hasPrevious || false
                })
                return data.content || data.items || []
            }
        },
        keepPreviousData: true,
    })

    // 데이터 새로고침 함수
    const refreshData = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['requests'] })
    }, [queryClient])

    const filtered = useMemo(() => {
        return listQuery.data || []
    }, [listQuery.data])

    // 조직 범위 옵션 로드 (내 조직 + 하위 조직)
    useEffect(() => {
        let ignore = false
        async function loadOrgScope() {
            try {
                const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
                const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
                if (!ignore) setOrgScopeOptions(Array.isArray(payload) ? payload : [])
            } catch {
                if (!ignore) setOrgScopeOptions([])
            }
        }
        loadOrgScope()
        return () => { ignore = true }
    }, [])

    const designerOptions = useMemo(() => {
        const names = new Set()
        for (const r of filtered) {
            if (r && r.designerName) names.add(r.designerName)
        }
        return Array.from(names).slice(0, 50)
    }, [filtered])

    const insurerOptions = useMemo(() => {
        const names = new Set()
        for (const r of filtered) {
            if (r && r.insurerName) names.add(r.insurerName)
        }
        return Array.from(names).slice(0, 50)
    }, [filtered])

    const applyPreset = useCallback((preset) => {
        const toDateStr = (d) => d.toISOString().slice(0, 10)
        if (!preset) {
            setPeriodStart('')
            setPeriodEnd('')
            return
        }
        const now = new Date()
        const end = toDateStr(now)
        if (preset === 'today') {
            const start = toDateStr(now)
            setPeriodStart(start)
            setPeriodEnd(end)
            return
        }
        const days = preset === '7d' ? 7 : 30
        const startDate = new Date(now)
        startDate.setDate(startDate.getDate() - days)
        setPeriodStart(toDateStr(startDate))
        setPeriodEnd(end)
    }, [])

    const isAllSelected = selected.length > 0 && filtered.every((r) => selected.includes(r.id))
    const toggleAll = () => {
        if (isAllSelected) setSelected([])
        else setSelected(filtered.map((r) => r.id))
    }
    const toggleOne = (id) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    }

    // 벌크 재배정 핸들러
    const handleBulkReassign = useCallback(async () => {
        if (selected.length === 0 || !bulkManagerId) return

        try {
            await api.post('/targets/reassign', {
                requestIds: selected,
                newManagerId: Number(bulkManagerId),
                reason: '벌크 재배정'
            })
            setSelected([])
            setBulkManagerId('')
            show('벌크 재배정 요청 완료', 'success')
            refreshData()
        } catch (err) {
            show('벌크 재배정 실패', 'error')
        }
    }, [selected, bulkManagerId, show, refreshData])

    const header = (
        <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-3">
                <div className="flex flex-col md:flex-row md:items-center gap-2 text-sm text-slate-600">
                    <span>총 {pagination.totalElements}건 (페이지 {page} / {pagination.totalPages})</span>
                    <div className="hidden md:flex items-center gap-2">
                        <span className="text-slate-600">조직</span>
                        <select
                            className="rounded border px-2 py-1 text-sm"
                            value={orgScopeId}
                            onChange={(e) => { setOrgScopeId(e.target.value); setPage(1) }}
                        >
                            <option value="">전체보기</option>
                            {orgScopeOptions.map(o => (
                                <option key={o.id} value={o.id}>{o.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {isOperator && (
                    <div className="hidden md:flex items-center gap-2 text-sm">
                        <span className="text-slate-600">관점</span>
                        <div className="inline-flex rounded border overflow-hidden">
                            <button type="button" className={`px-3 py-1 ${view==='GA'?'bg-brand-navy text-white':'bg-white'}`} onClick={() => setView('GA')}>GA</button>
                            <button type="button" className={`px-3 py-1 ${view==='INSURER'?'bg-brand-navy text-white':'bg-white'}`} onClick={() => setView('INSURER')}>원수사</button>
                            <button type="button" className={`px-3 py-1 ${view==='ALL'?'bg-brand-navy text-white':'bg-white'}`} onClick={() => setView('ALL')}>전체</button>
                        </div>
                    </div>
                )}
                <button
                    className="hidden md:inline-flex rounded border px-3 py-2 text-sm"
                    onClick={async () => {
                        try {
                            const res = await api.get('/requests/export', {
                                params: {
                                    q: dq || undefined,
                                    status: status !== 'ALL' ? status : undefined,
                                    sort,
                                    page,
                                    size,
                                    view,
                                    designer: designer || undefined,
                                    insurer: insurer || undefined,
                                    from: periodStart || undefined,
                                    to: periodEnd || undefined
                                },
                                responseType: 'blob'
                            })
                            const url = URL.createObjectURL(res.data)
                            const a = document.createElement('a')
                            a.href = url
                            a.download = 'requests.csv'
                            a.click()
                            URL.revokeObjectURL(url)
                        } catch { }
                    }}
                >
                    CSV 내보내기
                </button>
            </div>

            <div className="hidden md:flex items-center gap-2">
                <input
                    className="rounded border px-3 py-2 text-sm w-40"
                    placeholder="새 매니저 ID"
                    value={bulkManagerId}
                    onChange={(e) => setBulkManagerId(e.target.value)}
                />
                <button
                    className="rounded border px-3 py-2 text-sm disabled:opacity-50"
                    disabled={selected.length === 0 || !bulkManagerId}
                    onClick={handleBulkReassign}
                >
                    선택 재배정
                </button>
                {selected.length > 0 && <span className="text-sm text-slate-600">선택 {selected.length}건</span>}
            </div>
        </div>
    )

    // 모바일 전용: 제목/원수사명만 표시 + 컬럼 간소화
    const tableMobile = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left whitespace-nowrap">생성일</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {filtered.map(r => (
                    <tr key={r.id} className="border-t hover:bg-slate-50">
                        <td className="px-4 py-2">
                            <div className="font-medium text-slate-900 break-words">{r.title || '-'}</div>
                            {view !== 'INSURER' && (r.insurerSummary || r.insurerName) && (
                                <div className="mt-0.5 text-[11px] text-slate-500">{r.insurerSummary || r.insurerName}</div>
                            )}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">{formatDateTimeMinute(r.createdAt)}</td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                            <Link to={`/requests/${r.id}`} className="text-brand-navy hover:underline">상세</Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    // 관점별 테이블
    const tableGA = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-2 py-2 text-left"><input type="checkbox" checked={isAllSelected} onChange={toggleAll} /></th>
                    <th className="px-4 py-2 text-left">설계사</th>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left">상태</th>
                    <th className="px-4 py-2 text-left">원수사</th>
                    <th className="px-4 py-2 text-left">생성일</th>
                    <th className="px-4 py-2 text-left">담당자</th>
                    <th className="px-4 py-2 text-left">최근 업데이트</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {filtered.map(r => (
                    <tr key={r.id} className="border-t hover:bg-slate-50">
                        <td className="px-2 py-2"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} /></td>
                        <td className="px-4 py-2">{r.designerName || '-'}</td>
                        <td className="px-4 py-2">{r.title}</td>
                        <td className="px-4 py-2">
                            <span className={`px-2 py-1 rounded text-xs ${r.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                                r.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                                    r.status === 'RECEIVED' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                }`}>
                                {RequestStatusLabel[r.status] || r.status}
                            </span>
                        </td>
                        <td className="px-4 py-2">
                            <span
                                title={Array.isArray(r.insurerCompanies) && r.insurerCompanies.length > 0 ? r.insurerCompanies.join('\n') : ''}
                                className="cursor-help"
                            >
                                {r.insurerSummary || r.insurerName || '-'}
                            </span>
                        </td>
                        <td className="px-4 py-2">{formatDateTimeMinute(r.createdAt)}</td>
                        <td className="px-4 py-2">
                            <span
                                title={Array.isArray(r.managerNames) && r.managerNames.length > 0 ? r.managerNames.join('\n') : ''}
                                className="cursor-help"
                            >
                                {r.managerSummary || r.managerName || '-'}
                            </span>
                        </td>
                        <td className="px-4 py-2">{formatDateTimeMinute(r.updatedAt)}</td>
                        <td className="px-4 py-2 text-right">
                            <Link to={`/requests/${r.id}`} className="text-brand-navy hover:underline">상세</Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const tableInsurer = (
        <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
                <tr>
                    <th className="px-2 py-2 text-left"><input type="checkbox" checked={isAllSelected} onChange={toggleAll} /></th>
                    <th className="px-4 py-2 text-left">요청 GA</th>
                    <th className="px-4 py-2 text-left">제목</th>
                    <th className="px-4 py-2 text-left">배정 매니저</th>
                    <th className="px-4 py-2 text-left">상태</th>
                    <th className="px-4 py-2 text-left">생성일</th>
                    <th className="px-4 py-2 text-left">완료일</th>
                    <th className="px-4 py-2"></th>
                </tr>
            </thead>
            <tbody>
                {filtered.map(r => (
                    <tr key={r.id} className="border-t hover:bg-slate-50">
                        <td className="px-2 py-2"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleOne(r.id)} /></td>
                        <td className="px-4 py-2">{r.gaOrgName || '-'}</td>
                        <td className="px-4 py-2">{r.title}</td>
                        <td className="px-4 py-2">{r.managerName || '-'}</td>
                        <td className="px-4 py-2">{RequestStatusLabel[r.status] || r.status}</td>
                        <td className="px-4 py-2">{formatDateTimeMinute(r.createdAt)}</td>
                        <td className="px-4 py-2">{r.completedAt ? formatDateTimeMinute(r.completedAt) : '-'}</td>
                        <td className="px-4 py-2 text-right">
                            <Link to={`/requests/${r.id}`} className="text-brand-navy hover:underline">상세</Link>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    )

    const footer = (
        <div className="flex items-center justify-between p-3 border-t text-sm">
            <button
                className="rounded border px-3 py-1 disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
                이전
            </button>
            <div className="flex items-center gap-2">
                <span>페이지 {page} / {pagination.totalPages}</span>
                <span className="text-slate-500">({pagination.totalElements}건)</span>
            </div>
            <button
                className="rounded border px-3 py-1 disabled:opacity-50"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
            >
                다음
            </button>
        </div>
    )

    return (
        <section>
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold">설계 의뢰 관리</h1>
                {/* GA 소속 사용자만 새 설계의뢰 노출 */}
                {view === 'GA' && (
                    <Link to="/requests/new" className="rounded bg-brand-navy text-white px-3 py-2 text-sm">+ 새 설계의뢰</Link>
                )}
            </div>
            {/* 모바일: 제목 검색 + 원수사명만 */}
            <div className="mt-4 flex flex-col gap-2 md:hidden">
                <input
                    className="w-full rounded border px-3 py-2 text-sm"
                    placeholder="제목 검색"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                {view !== 'INSURER' && (
                    <input
                        className="w-full rounded border px-3 py-2 text-sm"
                        placeholder="원수사명"
                        value={insurer}
                        onChange={(e) => setInsurer(e.target.value)}
                        list="insurer-list"
                    />
                )}
            </div>

            {/* 데스크톱: 기존 필터 그대로 */}
            <div className="mt-4 hidden md:flex items-center gap-2 flex-wrap">
                <input
                    className="w-full md:w-80 rounded border px-3 py-2 text-sm"
                    placeholder="검색: 제목"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <select className="rounded border px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="ALL">전체</option>
                    <option value="RECEIVED">RECEIVED</option>
                    <option value="IN_PROGRESS">IN_PROGRESS</option>
                    <option value="COMPLETED">COMPLETED</option>
                </select>
                {/* 관점별 필터 확장 */}
                {view !== 'INSURER' && (
                    <>
                        <input
                            className="rounded border px-3 py-2 text-sm w-40"
                            placeholder="설계사 이름"
                            value={designer}
                            onChange={(e) => setDesigner(e.target.value)}
                        />
                        <datalist id="designer-list">
                            {designerOptions.map(name => (<option key={name} value={name} />))}
                        </datalist>
                        <input
                            className="rounded border px-3 py-2 text-sm w-40"
                            placeholder="원수사명"
                            value={insurer}
                            onChange={(e) => setInsurer(e.target.value)}
                        />
                        <datalist id="insurer-list">
                            {insurerOptions.map(name => (<option key={name} value={name} />))}
                        </datalist>
                    </>
                )}

                {/* 보험사 사용자 전용: 보기 범위 토글 */}
                {view === 'INSURER' && isInsurerUser && (
                    <div className="flex items-center gap-2 text-xs md:text-sm">
                        <span className="text-slate-600">보기 범위</span>
                        <div className="inline-flex rounded border overflow-hidden">
                            <button
                                type="button"
                                className={`px-3 py-1 ${insurerScope === 'MINE' ? 'bg-brand-navy text-white' : 'bg-white'}`}
                                onClick={() => { setInsurerScope('MINE'); setPage(1) }}
                            >
                                나에게 배정된 의뢰
                            </button>
                            <button
                                type="button"
                                className={`px-3 py-1 ${insurerScope === 'BRANCH' ? 'bg-brand-navy text-white' : 'bg-white'}`}
                                onClick={() => { setInsurerScope('BRANCH'); setPage(1) }}
                            >
                                우리 지점 전체 의뢰
                            </button>
                        </div>
                    </div>
                )}
                <input
                    type="date"
                    className="rounded border px-3 py-2 text-sm"
                    value={periodStart}
                    onChange={(e) => setPeriodStart(e.target.value)}
                />
                <input
                    type="date"
                    className="rounded border px-3 py-2 text-sm"
                    value={periodEnd}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                />
                <select className="rounded border px-3 py-2 text-sm" value={periodPreset} onChange={(e) => { setPeriodPreset(e.target.value); applyPreset(e.target.value) }}>
                    <option value="">직접 설정</option>
                    <option value="today">오늘</option>
                    <option value="7d">최근 7일</option>
                    <option value="30d">최근 30일</option>
                </select>
                <select className="rounded border px-3 py-2 text-sm" value={size} onChange={(e) => setSize(Number(e.target.value))}>
                    <option value={10}>10개</option>
                    <option value={20}>20개</option>
                    <option value={50}>50개</option>
                </select>
                <select className="rounded border px-3 py-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
                    <option value="createdAt,DESC">최근 생성 순</option>
                    <option value="createdAt,ASC">오래된 생성 순</option>
                    <option value="updatedAt,DESC">최근 업데이트 순</option>
                    <option value="updatedAt,ASC">오래된 업데이트 순</option>
                </select>
            </div>

            <div className="mt-4">
                <ListContainer
                    loading={listQuery.isFetching}
                    error={listQuery.isError ? '목록을 불러오지 못했습니다' : null}
                    onRetry={() => listQuery.refetch()}
                    header={header}
                    table={
                        <>
                            <div className="md:hidden">{tableMobile}</div>
                            <div className="hidden md:block">{view === 'INSURER' ? tableInsurer : tableGA}</div>
                        </>
                    }
                    footer={footer}
                />
            </div>
        </section>
    )
}


